// Alquiler de unidades del edificio ("Alquiler del edificio", 30-oct-2026).
//
// Contrato: un doc por unidad en `alquileres/{apartmentId}`:
//   { buildingid, apartmentId, apartmentNumber, targetFundId, mesCobro, tramos, active, ... }
//   - mesCobro "adelantado": el cierre de M (que se paga en M+1) cobra el alquiler de M+1.
//     "vencido": el cierre de M cobra el alquiler de M.
//   - tramos: [{ desde: "YYYY-MM", hasta: "YYYY-MM" | null, monto, nota }]. Monto fijo
//     en pesos enteros por período; el ajuste por inflación lo carga el admin como
//     un tramo nuevo (decisión de Fefo: solo tramos manuales).
// El alquiler nunca genera mora y lo cobrado entra como INGRESO al fondo del contrato.
// Funciones puras: el cierre (CierreMes) y las pantallas del admin las usan.

import { roundPesos } from "./redondeoPesos";
import { esAlquilerDelEdificio } from "./unidadesEdificio";

export const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
export const MES_COBRO_ADELANTADO = "adelantado";
export const MES_COBRO_VENCIDO = "vencido";
export const MESES_COBRO = [MES_COBRO_ADELANTADO, MES_COBRO_VENCIDO];

const RE_PERIODO = /^\d{4}-(0[1-9]|1[0-2])$/;

/** "YYYY-MM" de un mes (1-12) y año. */
export const periodoKey = (month, year) => `${year}-${String(month).padStart(2, "0")}`;

/** "Noviembre 2026" de un período { month, year } o "YYYY-MM". */
export function nombrePeriodo(p) {
  if (!p) return "";
  if (typeof p === "string") {
    if (!RE_PERIODO.test(p)) return p;
    const [y, m] = p.split("-").map(Number);
    return `${MESES[m - 1]} ${y}`;
  }
  return `${MESES[p.month - 1] || ""} ${p.year}`.trim();
}

/** Mes siguiente de { month, year } (diciembre → enero del año siguiente). */
export function mesSiguiente({ month, year }) {
  return month === 12 ? { month: 1, year: year + 1 } : { month: month + 1, year };
}

/** Mes anterior de "YYYY-MM" (enero → diciembre del año anterior). */
export function mesAnteriorKey(periodo) {
  const [y, m] = periodo.split("-").map(Number);
  return m === 1 ? periodoKey(12, y - 1) : periodoKey(m - 1, y);
}

/**
 * Qué alquiler cobra el cierre de { month, year } según el contrato.
 * @returns {{ month: number, year: number }}
 */
export function periodoDeAlquiler(contrato, month, year) {
  return contrato?.mesCobro === MES_COBRO_VENCIDO ? { month, year } : mesSiguiente({ month, year });
}

/** Tramo del contrato que cubre el período "YYYY-MM"; null si ninguno. */
export function tramoVigente(contrato, periodo) {
  const tramos = Array.isArray(contrato?.tramos) ? contrato.tramos : [];
  return tramos.find((t) => t && t.desde <= periodo && (!t.hasta || periodo <= t.hasta)) || null;
}

/**
 * Errores de una lista de tramos (vacía si está bien): formato YYYY-MM, desde ≤
 * hasta, monto en pesos enteros > 0, sin superponerse (solo el último puede
 * quedar abierto).
 * @returns {string[]}
 */
export function validarTramos(tramos) {
  const errores = [];
  const lista = Array.isArray(tramos) ? tramos : [];
  lista.forEach((t, i) => {
    const n = `Tramo ${i + 1}`;
    if (!RE_PERIODO.test(t?.desde || "")) errores.push(`${n}: el mes "desde" no es válido.`);
    if (t?.hasta != null && t.hasta !== "" && !RE_PERIODO.test(t.hasta)) errores.push(`${n}: el mes "hasta" no es válido.`);
    if (t?.hasta && t?.desde && t.hasta < t.desde) errores.push(`${n}: "hasta" es anterior a "desde".`);
    const monto = Number(t?.monto);
    if (!(monto > 0) || roundPesos(monto) !== monto) errores.push(`${n}: el monto tiene que ser en pesos enteros y mayor a 0.`);
  });
  if (errores.length) return errores;
  const ordenados = [...lista].sort((a, b) => a.desde.localeCompare(b.desde));
  for (let i = 1; i < ordenados.length; i += 1) {
    const prev = ordenados[i - 1];
    if (!prev.hasta || prev.hasta >= ordenados[i].desde) {
      errores.push(`Los tramos desde ${nombrePeriodo(prev.desde)} y desde ${nombrePeriodo(ordenados[i].desde)} se superponen.`);
    }
  }
  return errores;
}

const tieneValor = (v) => v != null && v !== "" && String(v).trim() !== "";

/**
 * Líneas de alquiler que cobra un cierre y avisos para el admin.
 *
 * @param {object} args
 * @param {Array} args.contratos - docs de `alquileres` del edificio ({ id, ...data })
 * @param {Array} args.apartmentsAll - todas las unidades del edificio
 * @param {object} [args.occByApt] - tenencias del período por unidad (tenenciasPorUnidad)
 * @param {number} args.month - mes del cierre
 * @param {number} args.year - año del cierre
 * @param {string} [args.cierreId] - id del cierre en curso (sus pagos no cuentan como "ya cobrado")
 * @param {Array} [args.pagosPrevios] - pagos del edificio (para no cobrar dos veces un período)
 * @param {Array} [args.ingresosManuales] - buildingincomes del edificio (aviso de doble conteo)
 * @param {string[]} [args.fondosActivos] - ids de fondos activos (caja, reserva, custom)
 * @returns {{ lineas: Array<{contratoId, apartmentId, apartmentNumber, userid, monto, fundId, periodo}>, avisos: string[] }}
 */
export function lineasDeAlquilerDelCierre({
  contratos = [],
  apartmentsAll = [],
  occByApt = {},
  month,
  year,
  cierreId = null,
  pagosPrevios = [],
  ingresosManuales = [],
  fondosActivos = ["caja", "reserva"],
}) {
  const lineas = [];
  const avisos = [];
  for (const apt of apartmentsAll.filter(esAlquilerDelEdificio)) {
    const unidad = `Unidad ${apt.number || apt.id}`;
    const contrato = contratos.find((c) => String(c.apartmentId || c.id) === String(apt.id));
    if (!contrato) {
      avisos.push(`${unidad} (Alquiler del edificio): no tiene contrato cargado, no se cobra alquiler.`);
      continue;
    }
    if (contrato.active === false) continue; // contrato dado de baja: no se cobra ni se avisa
    const periodo = periodoDeAlquiler(contrato, month, year);
    const nombre = nombrePeriodo(periodo);
    const tramo = tramoVigente(contrato, periodoKey(periodo.month, periodo.year));
    if (!tramo) {
      avisos.push(`${unidad}: el contrato no tiene tramo para ${nombre}, no se cobra alquiler. Cargá el tramo en la unidad.`);
      continue;
    }
    if (!tieneValor(apt.tenant)) {
      avisos.push(`${unidad}: no tiene inquilino, no se cobra el alquiler de ${nombre}.`);
      continue;
    }
    const contratoId = String(contrato.id || contrato.apartmentId);
    const yaCobrado = pagosPrevios.some((p) =>
      String(p.rentContractId || "") === contratoId &&
      p.rentPeriod?.month === periodo.month && p.rentPeriod?.year === periodo.year &&
      (!cierreId || p.montlyaccountId !== cierreId)
    );
    if (yaCobrado) {
      avisos.push(`${unidad}: el alquiler de ${nombre} ya se cobró en otro cierre, no se vuelve a cobrar.`);
      continue;
    }
    let fundId = contrato.targetFundId || "reserva";
    if (!fondosActivos.includes(fundId)) {
      avisos.push(`${unidad}: el fondo del contrato no está activo; el alquiler de ${nombre} va a Caja.`);
      fundId = "caja";
    }
    const manual = ingresosManuales.some((inc) =>
      inc?.type === "alquiler" && inc.status !== "anulado" &&
      inc.period?.month === periodo.month && inc.period?.year === periodo.year
    );
    if (manual) {
      avisos.push(`${unidad}: hay un ingreso manual de alquiler de ${nombre} en Ingresos del edificio. Si es el mismo alquiler, se contaría dos veces: anulá uno de los dos.`);
    }
    const inquilinos = new Set((occByApt[String(apt.id)]?.tenant || []).map((t) => String(t.userid)).filter(Boolean));
    if (inquilinos.size > 1) {
      avisos.push(`${unidad}: cambió el inquilino en el período; el alquiler de ${nombre} se le cobra entero al inquilino actual.`);
    }
    lineas.push({
      contratoId,
      apartmentId: apt.id,
      apartmentNumber: apt.number || null,
      userid: String(apt.tenant).trim(),
      monto: roundPesos(tramo.monto),
      fundId,
      periodo,
    });
  }
  return { lineas, avisos };
}

/**
 * Avisos de un contrato para el admin: el tramo del próximo alquiler a cobrar
 * falta o vence pronto (dentro de `diasAviso`).
 * @param {object} contrato
 * @param {{ hoy?: Date, diasAviso?: number }} [opts]
 * @returns {string[]}
 */
export function alertasDeAlquiler(contrato, { hoy = new Date(), diasAviso = 30 } = {}) {
  if (!contrato || contrato.active === false) return [];
  const alertas = [];
  // El próximo cierre es el del mes en curso: cobra el alquiler de este mes o del siguiente.
  const proximo = periodoDeAlquiler(contrato, hoy.getMonth() + 1, hoy.getFullYear());
  const tramo = tramoVigente(contrato, periodoKey(proximo.month, proximo.year));
  if (!tramo) {
    alertas.push(`Falta el tramo de ${nombrePeriodo(proximo)}: el próximo cierre no va a cobrar alquiler.`);
    return alertas;
  }
  if (tramo.hasta) {
    const [y, m] = tramo.hasta.split("-").map(Number);
    const finTramo = new Date(y, m, 0); // último día del mes "hasta"
    const dias = Math.ceil((finTramo - hoy) / 86400000);
    const siguiente = mesSiguiente({ month: m, year: y });
    const haySiguiente = !!tramoVigente(contrato, periodoKey(siguiente.month, siguiente.year));
    if (!haySiguiente && dias <= diasAviso) {
      alertas.push(`El tramo actual termina en ${nombrePeriodo(tramo.hasta)} y no hay tramo para ${nombrePeriodo(siguiente)}: cargá el monto nuevo.`);
    }
  }
  return alertas;
}

/**
 * Ajuste del monto sin dar de baja el contrato: los tramos que siguen vigentes en
 * `desde` se cierran el mes anterior y se agrega un tramo nuevo sin fin. No pisa
 * meses que ya tienen otro monto cargado a partir de `desde` (eso se corrige en
 * "Editar contrato").
 * @param {object} contrato
 * @param {{ desde: string, monto: number, nota?: string }} ajuste
 * @returns {{ tramos: Array, errores: string[] }}
 */
export function ajustarMonto(contrato, { desde, monto, nota = "" }) {
  const actuales = Array.isArray(contrato?.tramos) ? contrato.tramos : [];
  const errores = [];
  if (!RE_PERIODO.test(desde || "")) errores.push("Elegí desde qué mes rige el monto nuevo.");
  const montoNum = Number(monto);
  if (!(montoNum > 0) || roundPesos(montoNum) !== montoNum) errores.push("El monto nuevo tiene que ser en pesos enteros y mayor a 0.");
  if (errores.length) return { tramos: actuales, errores };
  if (actuales.some((t) => t.desde >= desde)) {
    return {
      tramos: actuales,
      errores: [`Ya hay un monto cargado desde ${nombrePeriodo(desde)} o después. Para cambiarlo usá "Editar contrato".`],
    };
  }
  const hastaAnterior = mesAnteriorKey(desde);
  const tramos = actuales
    .map((t) => (!t.hasta || t.hasta >= desde ? { ...t, hasta: hastaAnterior } : { ...t }))
    .concat({ desde, hasta: null, monto: montoNum, nota: String(nota || "").trim() })
    .sort((a, b) => a.desde.localeCompare(b.desde));
  return { tramos, errores: validarTramos(tramos) };
}
